import React, { useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'

import CheckBox from 'src/components/CheckBox'
import Tooltip  from 'src/components/Tooltip'

const DataSharing = ({
  foundation, partner_is_pledge, data_sharing, collect_address, collect_phone,
  beneficiary, partner, defaultShares, shares, setShares, i18n,
}) => {
  const singleKey = useMemo(() => {
    if (!foundation && beneficiary.type != 'Crowdfund')
      return 'partner'
    if (partner.organization_id == beneficiary.id ||
        // Special rules for Pledge partner
        partner_is_pledge)
      return 'organization'
  }, [ foundation, partner_is_pledge, beneficiary, partner ])

  const key = useMemo(() =>
    singleKey || `partner_and_${beneficiary.type.toLowerCase()}`
  , [ singleKey, beneficiary ])

  const organizer = useMemo(() =>
    partner.business ? partner.name : i18n.t('personal_data_sharing.organizer')
  , [ partner ])

  const options = useMemo(() => {
    const whom = i18n.t(`personal_data_sharing.${key}`, { partner: organizer })
    const extended = collect_address || collect_phone ? '_extended' : ''

    switch (data_sharing.display) {
    case 'split_fundraiser':
      return [
        {
          key,
          info: defaultShares.filter(s => s.includes('partner') || s.includes('beneficiary')),
          labelText: i18n.t(`personal_data_sharing.${data_sharing.behavior}${extended}`, { whom }),
          extended,
        },
        {
          key: 'public',
          info: defaultShares.filter(s => s.includes('public')),
          labelText: i18n.t(`personal_data_sharing.${data_sharing.behavior}_public`),
        },
      ]
    case 'split':
    case 'split_with_public':
      // Fall through otherwise
      if (!singleKey) {
        const checkboxes = [
          {
            key: 'partner',
            whom: i18n.t('personal_data_sharing.partner', { partner: organizer }),
            extended: '',
          },
          {
            key: 'beneficiary',
            whom: i18n.t(`personal_data_sharing.${beneficiary.type.toLowerCase()}`),
            extended,
          },
        ].map(({ key, whom, extended }) => {
          const info = defaultShares.filter(s => s.includes(key))
          const labelText =
          i18n.t(`personal_data_sharing.${data_sharing.behavior}_split.${key}`, { whom })
          || i18n.t(`personal_data_sharing.${data_sharing.behavior}${extended}`, { whom })
          return { key, info, labelText, extended }
        })
        if (data_sharing.display == 'split_with_public') {
          checkboxes.push({
            key: 'public',
            info: defaultShares.filter(s => s.includes('public')),
            labelText: i18n.t(`personal_data_sharing.${data_sharing.behavior}_public`),
          })
        }
        return checkboxes
      }
    case 'single_fundraiser':
    case 'single':
      const suffix = extended
      || (['single_fundraiser', 'split_with_public'].includes(data_sharing.display) ? '_with_public' : '')
      return [
        {
          key,
          extended,
          labelText: i18n.t(`personal_data_sharing.${data_sharing.behavior}${suffix}`, { whom }),
          info: defaultShares,
        }
      ]
    }
  }, [ data_sharing, collect_address, collect_phone, beneficiary, defaultShares ])

  return options?.map(({ key, info, labelText, extended }) => {
    const sharing = shares.includes(info[0])

    return (
      <CheckBox
        key={key}
        id={`personal-data-sharing-${key}`}
        checked={data_sharing.behavior == 'opt_out' ? !sharing : !!sharing}
        onChange={() => setShares(shares =>
          sharing ? shares.filter(s => !info.includes(s)) : shares.concat(info)
        )}
        label={<span dangerouslySetInnerHTML={{ __html: labelText }} />}
        afterLabel={
          extended &&
          <Tooltip html title={`
            <div class="text-start text-left">
              ${key.includes('partner') ? `
                <div>
                  <p class="mb-1">
                    ${i18n.t('personal_data_sharing.shared_with',
                      { whom: i18n.t('personal_data_sharing.partner',
                        { partner: organizer }) })}
                  </p>
                  <ul>
                    <li>${i18n.t('name')}</li>
                    <li>${i18n.t('email')}</li>
                  </ul>
                </div>
              ` : ''}
              <p class="mb-1">
                ${i18n.t('personal_data_sharing.shared_with',
                  { whom: i18n.t('personal_data_sharing.organization') })}
              </p>
              <ul class="mb-1">
                <li>${i18n.t('name')}</li>
                <li>${i18n.t('email')}</li>
                ${collect_phone ? `<li>${i18n.t('phone')}</li>` : ''}
                ${collect_address ? `<li>${i18n.t('address')}</li>` : ''}
              </ul>
            </div>
          `}><FontAwesomeIcon icon={faInfoCircle} /></Tooltip>
        }
      />
    )
  })
}

export default DataSharing
